import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Swords, Crown, User, Copy, Check } from 'lucide-react';
import { useInterval } from '../../hooks/useInterval';
import { useI18n } from '../../contexts/I18nContext';

interface MatchDetails {
  id: string;
  password: string;
  status: 'pending' | 'active' | 'finished';
  player1_id: string;
  player2_id: string;
  player1: { username: string; avatar_url?: string };
  player2: { username: string; avatar_url?: string };
  winner_id?: string;
  player1_ready: boolean;
  player2_ready: boolean;
  result_proposal_type: 'victory' | 'defeat' | null;
  result_proposer_id: string | null;
  result_proposal_time: string | null;
  ready_time: string | null;
}

export function Game() {
  const { password } = useParams();
  const { user } = useAuth();
  const [match, setMatch] = useState<MatchDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [readyTimer, setReadyTimer] = useState<number | null>(null);
  const { t } = useI18n();

  const fetchMatch = async () => {
    try {
      // D'abord récupérer le match
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*')
        .eq('password', password)
        .single();

      if (matchError) {
        console.error(t('pvp_game.errors.fetch_match'), matchError);
        setLoading(false);
        return;
      }

      // Puis récupérer les profils séparément
      const [player1Profile, player2Profile] = await Promise.all([
        supabase
          .from('profiles')
          .select('username, avatar_url')
          .eq('user_id', matchData.player1_id)
          .single(),
        supabase
          .from('profiles')
          .select('username, avatar_url')
          .eq('user_id', matchData.player2_id)
          .single()
      ]);

      // Construire l'objet match complet
      const completeMatch = {
        ...matchData,
        player1: {
          username: player1Profile?.data?.username || t('pvp_game.player.player1'),
          avatar_url: player1Profile?.data?.avatar_url
        },
        player2: {
          username: player2Profile?.data?.username || t('pvp_game.player.player2'),
          avatar_url: player2Profile?.data?.avatar_url
        }
      };

      console.log('Match data:', completeMatch);
      setMatch(completeMatch);
      setLoading(false);
    } catch (error) {
      console.error(t('pvp_game.errors.match_fetch'), error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatch();

    const subscription = supabase
      .channel('match_updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'matches',
        filter: `password=eq.${password}`,
      }, async () => {
        await fetchMatch();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [password]);

  useEffect(() => {
    if (match?.result_proposal_time) {
      const endTime = new Date(match.result_proposal_time).getTime() + 60000; // +60 secondes
      const now = new Date().getTime();
      setRemainingTime(Math.max(0, Math.floor((endTime - now) / 1000)));
    } else {
      setRemainingTime(null);
    }
  }, [match?.result_proposal_time]);

  useInterval(() => {
    if (remainingTime !== null && remainingTime > 0) {
      setRemainingTime(remainingTime - 1);
    }
  }, 1000);

  useEffect(() => {
    if (remainingTime === 0 && match?.result_proposal_time && !match.winner_id) {
      // Validation automatique après 60 secondes
      handleResultConfirmation(true);
    }
  }, [remainingTime]);

  useEffect(() => {
    if (match?.status === 'pending' && match.ready_time) {
      const endTime = new Date(match.ready_time).getTime() + 60000; // +60 secondes
      const now = new Date().getTime();
      setReadyTimer(Math.max(0, Math.floor((endTime - now) / 1000)));
    } else {
      setReadyTimer(null);
    }
  }, [match?.status, match?.ready_time]);

  useInterval(() => {
    if (readyTimer !== null && readyTimer > 0) {
      setReadyTimer(readyTimer - 1);
    } else if (readyTimer === 0 && match?.status === 'pending') {
      // Annuler le match si le temps est écoulé
      cancelMatch();
    }
  }, 1000);

  const cancelMatch = async () => {
    if (!match) return;
    
    try {
      const { error } = await supabase
        .from('matches')
        .update({
          status: 'finished',
          player1_ready: false,
          player2_ready: false,
        })
        .eq('id', match.id);

      if (error) throw error;
      
      await fetchMatch();
    } catch (error) {
      console.error(t('pvp_game.errors.cancel_match'), error);
    }
  };

  const handleReady = async () => {
    if (!match || !user) return;
    
    const readyField = isPlayer1 ? 'player1_ready' : 'player2_ready';
    const updates: any = {
      [readyField]: true,
    };

    // Si c'est le premier joueur à être prêt, on définit ready_time
    if (!match.player1_ready && !match.player2_ready) {
      updates.ready_time = new Date().toISOString();
    }

    // Si les deux joueurs sont prêts (ou vont l'être), on passe en actif
    if (
      (isPlayer1 && match.player2_ready) || 
      (!isPlayer1 && match.player1_ready)
    ) {
      updates.status = 'active';
      updates.ready_time = null; // Réinitialiser ready_time
      // Nettoyer la file d'attente immédiatement quand la partie démarre
      await cleanupQueue();
    }

    const { error } = await supabase
      .from('matches')
      .update(updates)
      .eq('id', match.id);

    if (error) {
      console.error(t('pvp_game.errors.update_match'), error);
    }
  };

  const handleResultProposal = async (type: 'victory' | 'defeat') => {
    if (!match || !user) return;

    try {
      const { error } = await supabase
        .from('matches')
        .update({
          result_proposal_type: type,
          result_proposer_id: user.id,
          result_proposal_time: new Date().toISOString()
        })
        .eq('id', match.id)
        .select('*')  // Ajout de select pour s'assurer que la requête est valide
        .single();    // On s'attend à mettre à jour une seule ligne

      if (error) {
        console.error(t('pvp_game.errors.propose_result'), error);
        // Optionnel : ajouter une notification d'erreur pour l'utilisateur
      }
    } catch (error) {
      console.error(t('pvp_game.errors.result_proposal'), error);
    }
  };

  const handleResultConfirmation = async (accept: boolean) => {
    if (!match || !user || !match.result_proposal_type) return;

    try {
      // Si le résultat est contesté, on réinitialise juste les champs de proposition
      if (!accept) {
        const updates = {
          result_proposal_type: null,
          result_proposer_id: null,
          result_proposal_time: null
        };

        const { error } = await supabase
          .from('matches')
          .update(updates)
          .eq('id', match.id);

        if (error) {
          console.error(t('pvp_game.errors.confirm_result'), error);
          return;
        }
      } else {
        // Si le résultat est accepté, on termine le match comme avant
        const updates = {
          result_proposal_type: null,
          result_proposer_id: null,
          result_proposal_time: null,
          status: 'finished' as const,
          winner_id: match.result_proposal_type === 'victory' 
            ? match.result_proposer_id 
            : match.player1_id === match.result_proposer_id 
              ? match.player2_id 
              : match.player1_id
        };

        const { error } = await supabase
          .from('matches')
          .update(updates)
          .eq('id', match.id);

        if (error) {
          console.error(t('pvp_game.errors.confirm_result'), error);
          return;
        }
      }

      await fetchMatch();
    } catch (error) {
      console.error(t('pvp_game.errors.result_confirmation'), error);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(match?.password || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(t('pvp_game.errors.copy_password'), err);
    }
  };

  const cleanupQueue = async () => {
    if (!match) return;
    try {
      await supabase
        .from('queue')
        .delete()
        .in('player_id', [match.player1_id, match.player2_id]);
    } catch (error) {
      console.error(t('pvp_game.errors.clean_queue'), error);
    }
  };

  const renderGameContent = () => {
    if (!match) return null;
    
    if (match.status === 'pending') {
      const isReady = isPlayer1 ? match.player1_ready : match.player2_ready;
      const otherReady = isPlayer1 ? match.player2_ready : match.player1_ready;

      return (
        <div className="bg-white p-8 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-center space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">{t('pvp_game.prep.title')}</h3>
            {readyTimer !== null && (
              <p className="text-sm text-gray-500">
                {t('pvp_game.prep.remaining_time')} <span className="font-medium text-primary-600">{readyTimer}s</span>
              </p>
            )}
            {!isReady ? (
              <div className="space-y-4">
                <p className="text-gray-600">
                  {t('pvp_game.prep.ready_instructions')}
                </p>
                <button
                  onClick={handleReady}
                  className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  {t('pvp_game.prep.ready_button')}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent"></div>
                <p className="text-gray-600 animate-pulse">
                  {t('pvp_game.prep.waiting')} {otherReady ? t('pvp_game.prep.waiting_start') : t('pvp_game.prep.waiting_other')}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (match.status === 'active') {
      if (!match.result_proposal_type) {
        return (
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200">
            <div className="text-center space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">{t('pvp_game.result.title')}</h3>
              <p className="text-gray-600">
                {t('pvp_game.result.instructions')}
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => handleResultProposal('victory')}
                  className="flex-1 max-w-[200px] px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  {t('pvp_game.result.win_button')}
                </button>
                <button
                  onClick={() => handleResultProposal('defeat')}
                  className="flex-1 max-w-[200px] px-6 py-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                >
                  {t('pvp_game.result.lose_button')}
                </button>
              </div>
            </div>
          </div>
        );
      }

      if (match.result_proposer_id === user?.id) {
        return (
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200">
            <div className="text-center space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent"></div>
                <p className="text-gray-600">
                  {t('pvp_game.result.waiting_confirmation')}
                  {remainingTime !== null && (
                    <span className="ml-2 font-medium text-primary-600">({remainingTime}s)</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="bg-white p-8 rounded-lg border-2 border-gray-200">
          <div className="text-center space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">{t('pvp_game.result.confirmation_title')}</h3>
            <p className="text-gray-700">
              {match.result_proposal_type === 'victory' 
                ? t('pvp_game.result.opponent_claims_victory') 
                : t('pvp_game.result.opponent_claims_defeat')}
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleResultConfirmation(true)}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('pvp_game.result.confirm_button')}
              </button>
              <button
                onClick={() => handleResultConfirmation(false)}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('pvp_game.result.contest_button')}
              </button>
            </div>
            {remainingTime !== null && (
              <p className="text-sm text-gray-500">
                {t('pvp_game.result.time_remaining')} <span className="font-medium text-primary-600">{remainingTime}s</span>
              </p>
            )}
          </div>
        </div>
      );
    }

    if (match.status === 'finished') {
      return (
        <div className="bg-white p-8 rounded-lg border-2 border-gray-200">
          <div className="text-center space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">{t('pvp_game.finished.title')}</h3>
            {match.winner_id ? (
              <p className="text-gray-600">
                {match.winner_id === user?.id 
                  ? t('pvp_game.finished.victory_message')
                  : t('pvp_game.finished.defeat_message')}
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-red-600">{t('pvp_game.finished.cancelled')}</p>
                <p className="text-gray-600">
                  {t('pvp_game.finished.cancel_reason')}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{t('pvp_game.not_found.title')}</h1>
          <p className="text-gray-600">{t('pvp_game.not_found.message')}</p>
        </div>
      </div>
    );
  }

  const isPlayer1 = user?.id === match.player1_id;
  const player1Name = match.player1?.username || t('pvp_game.player.player1');
  const player2Name = match.player2?.username || t('pvp_game.player.player2');

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-primary-600 text-white p-6">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Swords className="w-6 h-6" />
            <h1 className="text-2xl font-bold">{t('pvp_game.title')}</h1>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 mb-2 rounded-full overflow-hidden border-2 border-white bg-gray-200 flex items-center justify-center shadow-md">
                  {match.player1.avatar_url ? (
                    <img
                      src={match.player1.avatar_url}
                      alt={`Avatar de ${player1Name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className={`text-xl font-semibold mb-1 
                  ${isPlayer1 ? 'text-white' : 'text-white/80'}`}>
                  {player1Name}
                </div>
                {match.status === 'pending' && (
                  match.player1_ready ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {t('pvp_game.prep.ready')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {t('pvp_game.prep.waiting')}
                    </span>
                  )
                )}
                {match.winner_id === match.player1_id && (
                  <Crown className="w-5 h-5 text-yellow-500 mt-2" />
                )}
              </div>
            </div>
            <div className="text-3xl font-bold mx-4 text-white">VS</div>
            <div className="text-center flex-1">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 mb-2 rounded-full overflow-hidden border-2 border-white bg-gray-200 flex items-center justify-center shadow-md">
                  {match.player2.avatar_url ? (
                    <img
                      src={match.player2.avatar_url}
                      alt={`Avatar de ${player2Name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className={`text-xl font-semibold mb-1
                  ${!isPlayer1 ? 'text-white' : 'text-white/80'}`}>
                  {player2Name}
                </div>
                {match.status === 'pending' && (
                  match.player2_ready ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {t('pvp_game.prep.ready')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {t('pvp_game.prep.waiting')}
                    </span>
                  )
                )}
                {match.winner_id === match.player2_id && (
                  <Crown className="w-5 h-5 text-yellow-500 mt-2" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Game Status */}
        <div className="p-6 space-y-8">
          <div className="space-y-6">
            <div className="text-center">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                ${match.status === 'active' ? 'bg-green-100 text-green-700' :
                  match.status === 'finished' ? 'bg-gray-100 text-gray-700' :
                  'bg-yellow-100 text-yellow-700'}`}>
                {match.status === 'active' ? t('pvp_game.status.active') :
                 match.status === 'finished' ? t('pvp_game.status.finished') : t('pvp_game.status.pending')}
              </span>
            </div>

            {/* N'afficher le mot de passe que lorsque la partie est active */}
            {match.status === 'active' && (
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex flex-col space-y-6">
                  {/* Section du mot de passe */}
                  <div className="text-center">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">{t('pvp_game.password.label')}</h3>
                    <div className="inline-flex items-center space-x-3">
                      <span className="text-2xl font-mono font-bold bg-white px-4 py-2 rounded-lg border-2 border-primary-100 text-primary-700">
                        {match.password}
                      </span>
                      <button
                        onClick={copyPassword}
                        className="p-2 hover:bg-white rounded-lg transition-colors border-2 border-transparent hover:border-primary-100"
                        title={t('pvp_game.password.copy_title')}
                      >
                        {copied ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-primary-600" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Instructions */}
                  <div className="border-t border-gray-200 pt-4">
                    <details className="text-sm">
                      <summary className="cursor-pointer hover:text-primary-600 font-medium flex items-center justify-center gap-2">
                        <span className="text-primary-600">{t('pvp_game.instructions.how_to_play')}</span>
                      </summary>
                      <div className="mt-4 bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h4 className="font-medium text-gray-900 mb-3">{t('pvp_game.instructions.title')}</h4>
                        <ol className="list-decimal space-y-3 pl-4 text-gray-600">
                          <li className="pl-2">
                            <span className="font-medium text-gray-800">{t('pvp_game.instructions.steps.step1_title')}</span>
                            <p className="text-sm mt-0.5">{t('pvp_game.instructions.steps.step1_detail')}</p>
                          </li>
                          <li className="pl-2">
                            <span className="font-medium text-gray-800">{t('pvp_game.instructions.steps.step2_title')}</span>
                            <p className="text-sm mt-0.5">{t('pvp_game.instructions.steps.step2_detail')}</p>
                          </li>
                          <li className="pl-2">
                            <span className="font-medium text-gray-800">{t('pvp_game.instructions.steps.step3_title')}</span>
                            <p className="text-sm mt-0.5">{t('pvp_game.instructions.steps.step3_detail')}</p>
                          </li>
                          <li className="pl-2">
                            <span className="font-medium text-gray-800">{t('pvp_game.instructions.steps.step4_title')}</span>
                            <p className="text-sm mt-0.5">{t('pvp_game.instructions.steps.step4_detail')}</p>
                          </li>
                          <li className="pl-2">
                            <span className="font-medium text-gray-800">{t('pvp_game.instructions.steps.step5_title')}</span>
                            <p className="text-sm mt-0.5">{t('pvp_game.instructions.steps.step5_detail')}</p>
                          </li>
                        </ol>
                        <div className="mt-4 bg-yellow-50 border border-yellow-100 rounded p-3 text-sm text-yellow-800">
                          <strong className="font-medium">{t('pvp_game.instructions.important')}</strong> {t('pvp_game.instructions.important_note')}
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            )}
          </div>

          {renderGameContent()}
        </div>
      </div>
    </div>
  );
}
